import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Sparkles, Upload, Trash2, X, Check, FileText, Loader2, Link2 } from "lucide-react";

const backend = process.env.REACT_APP_BACKEND_URL || "";
const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${backend}${u}` : u);

const BLOG_TAGS = ["News", "Recipes", "Catering", "Events", "Team"];

const inputCls = "w-full border border-ink bg-white px-3 py-2 font-serif text-sm";
const labelCls = "font-mono text-[10px] uppercase tracking-[0.2em] text-ink/70";

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/owner/upload-image", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.url;
}

export default function AdminBlogTab() {
  const [posts, setPosts] = useState([]);
  const [subject, setSubject] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [form, setForm] = useState({ title: "", excerpt: "", body: "", tag: "News", image_url: "" });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/owner/blog"); setPosts(data || []); }
    catch { toast.error("Couldn't load posts"); }
  };
  useEffect(() => { load(); }, []);

  const aiDraft = async () => {
    if (!subject.trim()) { toast.error("Enter a subject first"); return; }
    setDrafting(true);
    try {
      const { data } = await api.post("/owner/blog/draft", { subject });
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        excerpt: data.excerpt || f.excerpt,
        body: data.body || f.body,
        tag: data.tag || f.tag,
      }));
      toast.success("AI draft ready — review, edit, then publish");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "AI draft failed");
    } finally { setDrafting(false); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setForm((f) => ({ ...f, image_url: url }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const publish = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Title and body are required"); return; }
    setSaving(true);
    try {
      await api.post("/owner/blog", form);
      setForm({ title: "", excerpt: "", body: "", tag: "News", image_url: "" });
      setSubject("");
      toast.success("Post published to /blog");
      load();
    } catch { toast.error("Couldn't publish"); }
    finally { setSaving(false); }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try { await api.delete(`/owner/blog/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-10" data-testid="admin-blog-tab">
      {/* AI Draft */}
      <div className="border-2 border-ink bg-bone p-6">
        <h3 className="font-alfa text-2xl uppercase">Draft a Post</h3>
        <p className="mt-1 font-serif text-xs text-ink/60">
          Type a subject (e.g., "how to trim a brisket for competition") and let the pit's AI writer draft the whole post.
          You can also skip this and write it yourself below.
        </p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <input
            data-testid="blog-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject or headline…"
            className={inputCls + " flex-1"}
          />
          <button
            type="button"
            onClick={aiDraft}
            disabled={drafting}
            data-testid="blog-ai-draft"
            className="inline-flex items-center justify-center gap-2 border-2 border-ink bg-ink px-5 py-3 font-mono text-[11px] uppercase tracking-[0.24em] text-bone hover:bg-transparent hover:text-ink disabled:opacity-50"
          >
            {drafting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {drafting ? "Drafting…" : "AI Draft"}
          </button>
        </div>
      </div>

      {/* Post editor */}
      <div className="border-2 border-ink bg-white p-6">
        <div className="flex items-center gap-2">
          <FileText size={18} />
          <h3 className="font-alfa text-2xl uppercase">Blog Post</h3>
        </div>
        <p className="mt-1 font-serif text-xs text-ink/60">Copy-paste your own or edit the AI draft above. Fields marked * are required.</p>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <label className="block md:col-span-2"><span className={labelCls}>Title *</span>
            <input data-testid="blog-title" className={inputCls + " mt-1"} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </label>
          <label className="block md:col-span-2"><span className={labelCls}>Excerpt (1–2 sentence hook)</span>
            <input data-testid="blog-excerpt" className={inputCls + " mt-1"} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
          </label>
          <label className="block"><span className={labelCls}>Tag</span>
            <select data-testid="blog-tag" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} className={inputCls + " mt-1 cursor-pointer"}>
              {BLOG_TAGS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <div>
            <span className={labelCls}>Cover Image (optional)</span>
            <div className="mt-1 flex items-center gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 border-2 border-ink bg-ink px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-bone">
                <Upload size={12} /> {uploading ? "Uploading…" : "Upload"}
                <input type="file" accept="image/*" onChange={uploadImage} className="hidden" data-testid="blog-image-upload" />
              </label>
              {form.image_url && <img src={resolveUrl(form.image_url)} alt="preview" className="h-14 w-14 border border-ink object-cover" />}
              {form.image_url && (
                <button type="button" onClick={() => setForm((f) => ({ ...f, image_url: "" }))} className="inline-flex items-center gap-1 border border-ink px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-red-700 hover:text-bone hover:border-red-700" data-testid="blog-image-clear">
                  <X size={12} /> Clear
                </button>
              )}
            </div>
          </div>
          <label className="block md:col-span-2"><span className={labelCls}>Body * (plain text, double-newline for paragraph breaks)</span>
            <textarea rows={12} data-testid="blog-body" className={inputCls + " mt-1"} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
          </label>
        </div>

        <button
          type="button"
          onClick={publish}
          disabled={saving}
          data-testid="blog-publish"
          className="mt-6 inline-flex items-center gap-2 border-2 border-ink bg-ink px-8 py-3 font-alfa text-base uppercase tracking-[0.2em] text-bone hover:bg-transparent hover:text-ink disabled:opacity-50"
        >
          <Check size={16} /> {saving ? "Publishing…" : "Publish Post"}
        </button>
      </div>

      {/* Existing posts */}
      <div>
        <h3 className="font-alfa text-xl uppercase">Published Posts ({posts.length})</h3>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">Live on /blog — newest first.</p>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          {posts.map((p) => (
            <div key={p.id} data-testid={`blog-post-${p.id}`} className="border border-ink bg-white p-4">
              {p.image_url && <img src={resolveUrl(p.image_url)} alt={p.title} className="mb-3 aspect-[16/9] w-full border border-ink object-cover" />}
              <div className="flex items-center gap-2">
                <span className="inline-block border border-ink bg-ink px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-bone">{p.tag}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">{p.date}</span>
              </div>
              <p className="mt-2 font-alfa text-lg uppercase leading-tight">{p.title}</p>
              {p.excerpt && <p className="mt-1 font-serif text-sm italic opacity-75">{p.excerpt}</p>}
              <div className="mt-3 flex items-center gap-2">
                <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 border border-ink px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-ink hover:text-bone">View</a>
                <button
                  type="button"
                  onClick={async () => {
                    const url = `${window.location.origin}/blog/${p.slug}`;
                    try { await navigator.clipboard.writeText(url); toast.success("Link copied — paste anywhere"); }
                    catch { toast.error("Couldn't copy — try again"); }
                  }}
                  data-testid={`blog-copy-link-${p.id}`}
                  className="inline-flex items-center gap-1 border border-ink px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-ink hover:text-bone"
                >
                  <Link2 size={12} /> Copy Link
                </button>
                <button onClick={() => remove(p.id, p.title)} className="inline-flex items-center gap-1 border border-ink px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] hover:border-red-700 hover:bg-red-700 hover:text-bone" data-testid={`blog-delete-${p.id}`}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}
          {posts.length === 0 && <p className="col-span-full font-mono text-xs text-ink/50">No published posts yet — draft one above.</p>}
        </div>
      </div>
    </div>
  );
}
